import React from 'react';
import { 
  isQuizRequest, 
  isFlashcardRequest, 
  extractQuizTopic, 
  generateQuiz, 
  generateFlashcards, 
  saveQuizProgress, 
  saveFlashcardProgress 
} from '../utils/quizUtils';
import QuizMessage from './QuizMessage';
import FlashcardMessage from './FlashcardMessage';

/**
 * Helper component to integrate quiz and flashcard functionality with the chat interface
 */
const QuizIntegration = {
  /**
   * Check if a message is requesting a quiz
   * @param {string} message - The user message
   * @returns {boolean} - True if the message is requesting a quiz
   */
  isQuizRequest: (message) => {
    return isQuizRequest(message);
  },
  
  /**
   * Check if a message is requesting flashcards
   * @param {string} message - The user message
   * @returns {boolean} - True if the message is requesting flashcards
   */
  isFlashcardRequest: (message) => {
    return isFlashcardRequest(message);
  },
  
  /**
   * Extract the quiz/flashcard topic from a message
   * @param {string} message - The user message
   * @returns {string} - The extracted topic
   */
  extractTopic: (message) => {
    return extractQuizTopic(message);
  },
  
  /**
   * Generate a quiz response for a user message
   * @param {string} message - The user message
   * @returns {Object} - Quiz data object with type: 'quiz'
   */
  generateQuizResponse: (message) => {
    const topic = extractQuizTopic(message);
    // Parse number of questions and difficulty from the message
    let count;
    const countMatch = message.match(/\b(\d{1,2})\s*(?:questions?|qs?)\b/i);
    if (countMatch) {
      count = parseInt(countMatch[1], 10);
    }
    let difficulty = 'mixed';
    const diffMatch = message.match(/\b(easy|medium|hard)\b/i);
    if (diffMatch) {
      difficulty = diffMatch[1].toLowerCase();
    }
    const quizData = generateQuiz(topic, count, difficulty);
    return { ...quizData, type: 'quiz' };
  },
  
  /**
   * Generate a flashcard response for a user message
   * @param {string} message - The user message
   * @returns {Object} - Flashcard data object with type: 'flashcard'
   */
  generateFlashcardResponse: (message) => {
    const topic = extractQuizTopic(message);
    const flashcardData = generateFlashcards(topic);
    return { ...flashcardData, type: 'flashcard' };
  },
  
  /**
   * Determine the appropriate learning content type based on user message
   * @param {string} message - The user message
   * @returns {Object} - Learning content data (quiz or flashcard)
   */
  generateLearningContent: (message) => {
    if (isFlashcardRequest(message)) {
      return QuizIntegration.generateFlashcardResponse(message);
    } else {
      return QuizIntegration.generateQuizResponse(message);
    }
  },
  
  /**
   * Render the appropriate learning content component based on content type
   * @param {Object} contentData - The learning content data
   * @param {Function} onComplete - Callback when content is completed
   * @returns {JSX.Element} - The appropriate component
   */
  renderLearningContent: (contentData, onComplete) => {
    if (!contentData) return null;
    
    if (contentData.type === 'flashcard') {
      return <FlashcardMessage flashcardData={contentData} onComplete={onComplete} />;
    } else {
      return <QuizMessage quiz={contentData} onComplete={onComplete} />;
    }
  },
  
  /**
   * Render a quiz message component
   * @param {Object} quiz - The quiz data
   * @param {Function} onComplete - Callback when quiz is completed
   * @returns {JSX.Element} - The QuizMessage component
   */
  renderQuizMessage: (quiz, onComplete) => {
    return <QuizMessage quiz={quiz} onComplete={onComplete} />;
  },
  
  /**
   * Render a flashcard message component
   * @param {Object} flashcardData - The flashcard data
   * @param {Function} onComplete - Callback when flashcard session is completed
   * @returns {JSX.Element} - The FlashcardMessage component
   */
  renderFlashcardMessage: (flashcardData, onComplete) => {
    return <FlashcardMessage flashcardData={flashcardData} onComplete={onComplete} />;
  },
  
  /**
   * Save quiz results to localStorage
   * @param {string} userId - The user ID
   * @param {string} quizId - The quiz ID
   * @param {Object} results - The quiz results
   */
  saveQuizResults: (userId, quizId, results) => {
    saveQuizProgress(userId, quizId, results);
  },
  
  /**
   * Save flashcard progress to localStorage
   * @param {string} userId - The user ID
   * @param {string} setId - The flashcard set ID
   * @param {Object} progress - The flashcard progress
   */
  saveFlashcardProgress: (userId, setId, progress) => {
    saveFlashcardProgress(userId, setId, progress);
  },
  
  /**
   * Generate an AI response for quiz completion
   * @param {Object} results - The quiz results
   * @returns {string} - The AI response message
   */
  generateQuizCompletionResponse: (results) => {
    const answeredQuestions = Object.values(results).filter(r => r.answered).length;
    const correctAnswers = Object.values(results).filter(r => r.isCorrect).length;
    const accuracy = answeredQuestions > 0 ? Math.round((correctAnswers / answeredQuestions) * 100) : 0;
    
    let feedback;
    if (accuracy >= 90) {
      feedback = "Excellent work! You've mastered this topic!";
    } else if (accuracy >= 70) {
      feedback = "Great job! You have a solid understanding of this topic.";
    } else if (accuracy >= 50) {
      feedback = "Good effort! With a bit more practice, you'll improve your understanding.";
    } else {
      feedback = "Thanks for taking the quiz! This topic might need some more review.";
    }
    
    return `
      <h3>Quiz Results</h3>
      <p>${feedback}</p>
      <div class="quizResultsSummary">
        <p>You answered ${correctAnswers} out of ${answeredQuestions} questions correctly (${accuracy}% accuracy).</p>
      </div>
      <p>Would you like to try another quiz on a different topic?</p>
    `;
  },
  
  /**
   * Generate an AI response for flashcard completion
   * @param {Object} progress - The flashcard progress
   * @returns {string} - The AI response message
   */
  generateFlashcardCompletionResponse: (progress) => {
    const totalCards = Object.keys(progress).length;
    const viewedCards = Object.values(progress).filter(p => p.viewed).length;
    const flippedCards = Object.values(progress).filter(p => p.flipped).length;
    const completion = totalCards > 0 ? Math.round((viewedCards / totalCards) * 100) : 0;
    
    return `
      <h3>Flashcard Session Complete</h3>
      <p>Great job reviewing the flashcards!</p>
      <div class="flashcardResultsSummary">
        <p>You viewed ${viewedCards} out of ${totalCards} cards (${completion}% completion).</p>
        <p>You flipped ${flippedCards} cards to see their answers.</p>
      </div>
      <p>Would you like to try another flashcard set on a different topic?</p>
    `;
  }
};

export default QuizIntegration;
